import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { cn } from '@/shared/utils/cn'
import { useAuth } from '@/lib/auth'
import ModulesNavDropdown from './ModulesNavDropdown'

// Cubic-bezier easings must be a 4-tuple for framer-motion's Easing type;
// an inferred number[] is not assignable (TS2322 on the Variants object).
const EASE_STANDARD = [0.4, 0, 0.2, 1] as const

const menuContainerVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.05, delayChildren: 0.1 } },
  exit: { opacity: 0, transition: { staggerChildren: 0.03, staggerDirection: -1 } },
}

const menuItemVariants = {
  hidden: { opacity: 0, x: 20 },
  visible: { opacity: 1, x: 0, transition: { duration: 0.3, ease: EASE_STANDARD } },
  exit: { opacity: 0, x: 10, transition: { duration: 0.2 } },
}

// Each entry has either `to` (in-app React Router route) or `href`
// (external — opens in a new tab). All marketing surfaces live on
// app.gamedevbuddy.com after the consolidation. /docs points at the
// in-SPA DocsPage backed by cms_docs — the canonical docs surface.
type MarketingNavLink =
  | { to: string; href?: never; label: string; external?: false }
  | { href: string; to?: never; label: string; external: true }

const marketingNavLinks: MarketingNavLink[] = [
  { to: '/modules', label: 'Modules' },
  { to: '/news', label: 'News' },
  { to: '/faqs', label: 'FAQs' },
  { to: '/docs', label: 'Docs' },
  { to: '/roadmap', label: 'Roadmap' },
  { to: '/support', label: 'Support' },
]

export default function Navigation() {
  const [isOpen, setIsOpen] = useState(false)
  const [isScrolled, setIsScrolled] = useState(false)
  const { isLoggedIn } = useAuth()

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50)
    window.addEventListener('scroll', handleScroll, { passive: true })
    handleScroll()
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  return (
    <nav
      className={cn(
        // relative z-50 establishes a stacking context ABOVE page content so
        // the Modules mega-menu (absolute z-50 inside this nav) always paints
        // over the content. Required because the scrolled state below adds
        // `backdrop-blur-md` — backdrop-filter creates its own stacking
        // context on the nav, which would otherwise TRAP the dropdown's z-50
        // inside a z-auto context and let content (e.g. the detail-page hero's
        // `relative z-10`) paint over the menu. Hence the bug appeared only
        // after scrolling.
        'relative z-50 bg-dark-2 transition-all duration-300',
        isScrolled
          ? 'shadow-[0_5px_20px_0_rgba(0,0,0,0.7)] backdrop-blur-md'
          : 'shadow-[0_5px_15px_0_rgba(0,0,0,0.5)]',
      )}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <div className="flex items-center">
            <Link to="/" className="flex items-center gap-3">
              <img
                src="/images/gdb-logo.png"
                alt="GameDevBuddy Logo"
                width={40}
                height={40}
                className="w-10 h-10 object-contain"
              />
              {/* Logo text uses heading font via .marketing-chrome h* rule */}
              <span
                className="text-2xl text-white tracking-wider"
                style={{ fontFamily: 'var(--font-heading)' }}
              >
                GameDevBuddy
              </span>
            </Link>
          </div>

          {/* Desktop nav */}
          <div className="hidden md:block">
            <div className="ml-10 flex items-center gap-2">
              {marketingNavLinks.map((link) =>
                link.to === '/modules' ? (
                  <ModulesNavDropdown key={link.label} />
                ) : link.external ? (
                  <a
                    key={link.label}
                    href={link.href}
                    className="nav-link"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {link.label}
                  </a>
                ) : (
                  <Link key={link.label} to={link.to} className="nav-link">
                    {link.label}
                  </Link>
                ),
              )}
              {isLoggedIn ? (
                <Link
                  to="/account"
                  className="btn-godlike btn-godlike-primary btn-godlike-sm ml-4"
                >
                  My Account
                </Link>
              ) : (
                <Link
                  to="/login"
                  className="btn-godlike btn-godlike-primary btn-godlike-sm ml-4"
                >
                  Sign In
                </Link>
              )}
            </div>
          </div>

          {/* Mobile hamburger */}
          <div className="md:hidden">
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="text-muted-godlike hover:text-white"
              aria-label={isOpen ? 'Close menu' : 'Open menu'}
            >
              <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                {isOpen ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                )}
              </svg>
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        <AnimatePresence>
          {isOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.3, ease: EASE_STANDARD }}
              className="md:hidden overflow-hidden bg-dark-3 border-t border-[var(--color-dark-4)]"
            >
              <motion.div
                variants={menuContainerVariants}
                initial="hidden"
                animate="visible"
                exit="exit"
                className="px-2 pt-2 pb-3 space-y-1 sm:px-3"
              >
                {marketingNavLinks.map((link) => (
                  <motion.div key={link.label} variants={menuItemVariants}>
                    {link.external ? (
                      <a
                        href={link.href}
                        className="nav-link block"
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={() => setIsOpen(false)}
                      >
                        {link.label}
                      </a>
                    ) : (
                      <Link
                        to={link.to}
                        className="nav-link block"
                        onClick={() => setIsOpen(false)}
                      >
                        {link.label}
                      </Link>
                    )}
                  </motion.div>
                ))}
                <motion.div variants={menuItemVariants} className="text-center pt-2 pb-1">
                  {isLoggedIn ? (
                    <Link
                      to="/account"
                      onClick={() => setIsOpen(false)}
                      className="btn-godlike btn-godlike-primary"
                    >
                      My Account
                    </Link>
                  ) : (
                    <Link
                      to="/login"
                      onClick={() => setIsOpen(false)}
                      className="btn-godlike btn-godlike-primary"
                    >
                      Sign In
                    </Link>
                  )}
                </motion.div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </nav>
  )
}
